import type { Coordenadas, EnderecoEncontrado } from '@plane/shared';
import { useState } from 'react';

import { buscarEndereco } from '@/modules/site-content/services/geocode.service';
import { ApiRequestError } from '@/services/http-client';

/** Recorte do mapa, igual ao que a seção do showroom monta. */
const RAIO_LON = 0.02;
const RAIO_LAT = 0.01;
const CASAS = 6;

function urlDoMapa({ lat, lon }: Coordenadas): string {
  const caixa = [lon - RAIO_LON, lat - RAIO_LAT, lon + RAIO_LON, lat + RAIO_LAT].map((grau) =>
    Number(grau.toFixed(CASAS)),
  );

  return `https://www.openstreetmap.org/export/embed.html?bbox=${caixa.join('%2C')}&layer=mapnik&marker=${String(lat)}%2C${String(lon)}`;
}

/**
 * Acha o ponto no mapa a partir do endereço escrito.
 *
 * O desenho gira em torno de uma coisa: **mostrar o que foi encontrado antes de
 * aceitar**. Geocodificação erra — troca o número, cai na cidade vizinha, acha
 * uma rua de nome parecido — e um mapa errado não é um detalhe visual, é um
 * cliente dirigindo até o endereço errado. Por isso a busca não grava nada
 * sozinha: ela devolve o endereço como o serviço o entendeu, desenha o mapa, e
 * espera alguém dizer que é aquilo mesmo.
 */
export function BuscadorDeMapa({
  consulta,
  coordenadas,
  aoConfirmar,
}: {
  /** Endereço escrito, montado dos campos do formulário. */
  consulta: string;
  coordenadas: Coordenadas | null;
  aoConfirmar: (coordenadas: Coordenadas) => void;
}) {
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [achado, setAchado] = useState<EnderecoEncontrado | null>(null);

  async function buscar() {
    setErro(null);
    setAchado(null);
    setBuscando(true);

    try {
      setAchado(await buscarEndereco(consulta));
    } catch (falha) {
      setErro(
        falha instanceof ApiRequestError
          ? falha.message
          : 'Não foi possível buscar o endereço agora.',
      );
    } finally {
      setBuscando(false);
    }
  }

  const emUso = achado ?? coordenadas;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void buscar()}
          disabled={buscando || consulta.trim().length < 5}
          className="border border-clay px-4 py-2 text-sm transition-colors hover:border-brand-600 hover:text-brand-600 disabled:opacity-50"
        >
          {buscando ? 'Procurando…' : 'Procurar no mapa'}
        </button>

        <span className="text-xs text-ink-soft">Usa o endereço e o CEP preenchidos acima.</span>
      </div>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {achado && (
        <div className="border border-clay p-3">
          <p className="text-xs tracking-[0.12em] text-ink-soft uppercase">Encontrei isto</p>
          <p className="mt-1.5 text-sm">{achado.descricao}</p>

          <p className="mt-3 text-sm text-ink-soft">
            {achado.aproximado ? (
              <>
                <strong>Confira a quadra no mapa abaixo antes de usar.</strong> O mapa tem a rua,
                mas não o número da porta — o alfinete caiu no meio da via. Para o ponto exato, abra
                o mapa no celular na frente da loja e use as coordenadas à mão.
              </>
            ) : (
              <>
                <strong>Confira no mapa abaixo antes de usar.</strong> Se o alfinete não estiver na
                porta da marcenaria, corrija o endereço e procure de novo.
              </>
            )}
          </p>

          <button
            type="button"
            onClick={() => {
              aoConfirmar({ lat: achado.lat, lon: achado.lon });
              setAchado(null);
            }}
            className="mt-3 bg-brand-600 px-4 py-2 text-sm text-white"
          >
            É este lugar, usar no site
          </button>
        </div>
      )}

      {emUso && (
        <figure className="m-0">
          <iframe
            title={achado ? 'Prévia do endereço encontrado' : 'Mapa que está no site'}
            loading="lazy"
            referrerPolicy="no-referrer"
            src={urlDoMapa(emUso)}
            className="aspect-[16/10] w-full border border-clay"
          />
          <figcaption className="mt-1.5 text-xs text-ink-soft">
            {achado
              ? 'Prévia do que você acabou de procurar — ainda não está no site.'
              : `No site agora: ${String(emUso.lat)}, ${String(emUso.lon)}`}
          </figcaption>
        </figure>
      )}
    </div>
  );
}
